import React, { useState } from 'react';
import { RouteProps, useNavigate } from 'react-router-dom';
import SignUpForm from '../../components/SignUpForm/SignUpForm';
import LoginForm from '../../components/LoginForm/LoginForm';
import ChangePasswordForm from '../../components/ChangePasswordForm/ChangePasswordForm';
import { useTranslation } from 'react-i18next';
import ForgotPasswordForm from '../../components/ForgotPasswordForm/ForgotPasswordForm';
import PrimaryButton from '../PrimaryButton/PrimaryButton';

const FormStates = {
  LOGIN: 'LOGIN',
  SIGNUP: 'SIGNUP',
  CHANGE_PASSWORD: 'CHANGE_PASSWORD',
  FORGOT_PASSWORD: 'FORGOT_PASSWORD',
};

const LoginFlow: React.FC<RouteProps & { callbackFn?: any, skipOnboardingAfterSignup?: boolean }> = ({ callbackFn, skipOnboardingAfterSignup }) => {
  const { t } = useTranslation();
  const [formState, setFormState] = useState('LOGIN');
  const [username, setUsername] = useState('');
  const navigate = useNavigate();
  
  const handlePasswordChangeRequired = (username: string) => {
    setFormState(FormStates.CHANGE_PASSWORD)
    setUsername(username);
  };
  
  const setLoginPage = () => {
    setFormState(FormStates.LOGIN)
  };
  
  const toggleIsSignUp = () => {
    setFormState('SIGNUP');
  };

  const toggleForgot = () => setFormState('FORGOT_PASSWORD');

  const handleSignupSuccess = () => {
    if (callbackFn) {
      callbackFn();
    } else if (!skipOnboardingAfterSignup) {
      navigate('/onboarding');
    }
  };

  const renderForm = () => {
    switch (formState) {
      case FormStates.CHANGE_PASSWORD:
        return <ChangePasswordForm username={username} onPasswordChanged={setLoginPage} />;
      case FormStates.SIGNUP:
        return <SignUpForm callbackFn={handleSignupSuccess}/>;
      case FormStates.LOGIN:
        return <LoginForm onPasswordChangeRequired={handlePasswordChangeRequired} callbackFn={callbackFn}/>
      case FormStates.FORGOT_PASSWORD:
        return <ForgotPasswordForm onBackToLogin={setLoginPage} />;
      default:
        return <LoginForm onPasswordChangeRequired={handlePasswordChangeRequired} callbackFn={callbackFn}/>;
    }
  };  

  const renderButton = () => {
    switch (formState) {
      case FormStates.SIGNUP:
        return (
          <PrimaryButton 
            fn={setLoginPage} 
            text={t("registration.login")!} 
            actionName="Switch to Login Form"
          />
        );
      default: 
        return (
          <PrimaryButton 
            fn={toggleIsSignUp} 
            text={t("registration.signup")!} 
            actionName="Switch to Sign Up Form" 
          />
        );
    }
  };

  return (
    <div className="bg-card rounded-lg shadow-lg p-8 border border-gray-200 space-y-2 relative">
      <div className="absolute left-0 top-0 w-full h-3 rounded-t-lg bg-darker-gold-300" />
      {/* Form Content */}
      <div className="space-y-4">
        {renderForm()}
      </div>

      <p className="text-center text-gray-500">{t("global.or")}...</p>

      <div className="space-y-3">
        <div className="flex flex-col items-center space-y-3">
          {renderButton()}
          <button 
            onClick={toggleForgot}
            className="text-sm text-gray-500 hover:text-gray-700 transition-colors duration-200 underline"
          >
            {t("registration.forgot_password.text")}
          </button>
        </div>
      </div>
    </div>
  );
};

export default LoginFlow;

